import React, { useRef, useLayoutEffect } from 'react';
import { Button, Calendar, CalendarProps } from 'antd';
import dayjs from 'dayjs';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Moment } from 'moment';
import { Views } from 'conversional-big-calendar';
import { useIntl } from 'umi';

import styles from '../index.less';
import { WEEK_AGENDA_VIEW } from '@/components/Kanban/ServiceRequests/Calendar/WeekAgendaView';

const InlineDatePicker = ({value, onChange, disabledDate, view }: {
  value: any;
  onChange: (date: Date) => void;
  disabledDate?: any;
  view: string;
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const th = containerRef.current?.querySelector<HTMLElement>('thead th:first-child');
    if (th) {
      th.dataset.weekAbbr = intl.formatMessage({ id: 'common.weekAbbreviation', defaultMessage: 'W' });
    }
  });

  const onSelect = (date: Moment) => {
    onChange(date.toDate())
  };

  const headerRender = ({ value, onChange }) => {
    const currentMonth = dayjs(value).format("MMMM YYYY");
    return (
      <div className={styles.inlineDatePickerHeader}>
        <Button
          type={"text"}
          shape={"circle"}
          size={"small"}
          icon={<LeftOutlined />}
          onClick={() => onChange(value.clone().subtract(1, "month"))}
        />

        <span>{currentMonth}</span>
        <Button
          data-testid="availability-calendar-next-month-btn"
          type={"text"}
          size={"small"}
          shape={"circle"}
          icon={<RightOutlined/>}
          onClick={() => onChange(value.clone().add(1, "month"))}
        />
      </div>
    );
  }
  const dateFullCellRender = (date) => {
    const isInSelectedPeriod = [Views.WEEK, WEEK_AGENDA_VIEW].includes(view)  ?
      date.isAfter(dayjs(value).startOf("week")) &&
      date.isBefore(dayjs(value).endOf("week")) :
      date.isAfter(dayjs(value).startOf("day")) &&
      date.isBefore(dayjs(value).add(3, "day").startOf("day"));

    const isToday = date.isSame(dayjs(), "day");
    const isTodayInRange = isToday && isInSelectedPeriod;

    const style: React.CSSProperties = {
      padding: "4px",
      fontWeight: isToday ? 700 : 300,
    };

    if (isInSelectedPeriod) {
      style.backgroundColor = isTodayInRange
        ? "rgba(39,174,157,0.6)"
        : "rgba(39,174,157,0.47)";
    }
    if (isToday && !isTodayInRange) {
      style.color = "var(--ant-color-primary)";
      style.boxShadow = "0 0 0 1px currentColor";
    }

    const cellClassName = isTodayInRange
      ? styles.todayInRange
      : isToday
        ? styles.todayCell
        : undefined;

    return (
      <div className={cellClassName} style={style}>
        {date.format("DD")}
      </div>
    );
  };
  const isDateDisabled = date => {
    if (!disabledDate) return false
    return disabledDate(date)
  }
  const props: CalendarProps<any> = {}
  if (view !== Views.DAY) props.dateFullCellRender = dateFullCellRender
  return (
    <div ref={containerRef}>
      <Calendar
        fullscreen={false}
        onSelect={onSelect}
        value={dayjs(value)}
        className={styles.inlineDatePicker}
        headerRender={headerRender}
        disabledDate={isDateDisabled}
        showWeek={true}
        {...props}
      />
    </div>
  );
};

export default InlineDatePicker;
